import * as yup from 'yup';

import { select } from '../../../../constants/enum';
import i18n from '../../../../i18n/configs';

const urlValidation = yup.string().url(i18n.t('validation.error.url'));

const requiredValidation = yup
  .string()
  .required(i18n.t('validation.error.required'));

const checkboxAtLeastOneOf = function (value: boolean[]): boolean {
  return value.includes(true);
};

const checkboxUpToTwo = function (value: boolean[]): boolean {
  const trueCount = value.filter((v) => v).length;
  return trueCount <= 2;
};

export const mediaBasicShema = yup.object().shape({
  facebook: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number')),
  funnel: yup
    .array()
    .test(
      'checkboxAtLeastOneOf',
      i18n.t('validation.error.atLeastOneOf'),
      (value) => checkboxAtLeastOneOf(value as boolean[])
    )
    .test('checkboxUpToTwo', i18n.t('validation.error.upToTwo'), (value) =>
      checkboxUpToTwo(value as boolean[])
    ),
  genre: yup
    .array()
    .test(
      'checkboxAtLeastOneOf',
      i18n.t('validation.error.atLeastOneOf'),
      (value) => checkboxAtLeastOneOf(value as boolean[])
    )
    .test('checkboxUpToTwo', i18n.t('validation.error.upToTwo'), (value) =>
      checkboxUpToTwo(value as boolean[])
    ),
  genreOther: yup.string().when('genre.99', {
    is: (value: boolean) => value,
    otherwise: yup.string().nullable(),
    then: requiredValidation.max(250, i18n.t('validation.error.below250'))
  }),
  instagram: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number')),
  line: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number')),
  mediaName: requiredValidation.max(250, i18n.t('validation.error.below250')),
  mediaType: yup
    .number()
    .typeError(i18n.t('validation.error.required'))
    .required(i18n.t('validation.error.required')),
  note: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number')),
  overview: requiredValidation.max(2000, i18n.t('validation.error.below2000')),
  scaleEmailSubscribers: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number')),
  scaleMembers: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number')),
  scalePvMau: yup
    .string()
    .nullable()
    .matches(/^[0-9,]+$/, i18n.t('validation.error.number'))
    .required(i18n.t('validation.error.required')),
  scaleUu: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number')),
  tiktok: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number')),
  twitter: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number')),
  youtube: yup
    .string()
    .nullable()
    .matches(/^[0-9,]*$/, i18n.t('validation.error.number'))
});

export const mediaUserShema = yup.object().shape({
  merit: requiredValidation.max(250, i18n.t('validation.error.below250')),
  target: yup
    .array()
    .test(
      'checkboxAtLeastOneOf',
      i18n.t('validation.error.atLeastOneOf'),
      (value) => checkboxAtLeastOneOf(value as boolean[])
    )
    .test('checkboxUpToTwo', i18n.t('validation.error.upToTwo'), (value) =>
      checkboxUpToTwo(value as boolean[])
    ),
  targetDetail: yup
    .string()
    .nullable()
    .max(250, i18n.t('validation.error.below250')),
  targetKind: yup
    .number()
    .typeError(i18n.t('validation.error.required'))
    .required(i18n.t('validation.error.required'))
});

export const mediaServiceShema = yup.object().shape({
  adType: yup
    .array()
    .test(
      'checkboxAtLeastOneOf',
      i18n.t('validation.error.atLeastOneOf'),
      (value) => checkboxAtLeastOneOf(value as boolean[])
    ),
  adTypeOther: yup.string().when('adType.99', {
    is: (value: boolean) => value,
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .nullable()
      .max(250, i18n.t('validation.error.below250'))
  }),
  budget: yup
    .number()
    .oneOf([1, 2, 3, 4, 5], i18n.t('validation.error.required')),
  compensation: yup
    .array()
    .test(
      'checkboxAtLeastOneOf',
      i18n.t('validation.error.atLeastOneOf'),
      (value) => checkboxAtLeastOneOf(value as boolean[])
    ),
  dueDate: yup
    .string()
    .nullable()
    .max(250, i18n.t('validation.error.below250')),
  guaranteeFlg: yup.array().nullable(),
  holdingData: yup.string().when('holdingDataFlg', {
    is: (value: string) => value === '1',
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .nullable()
      .max(250, i18n.t('validation.error.below250'))
  }),
  holdingDataFlg: yup
    .string()
    .nullable()
    .required(i18n.t('validation.error.required')),
  mediaMaterialUpdateCycle: yup.string().nullable(),
  otherStrength: yup
    .string()
    .nullable()
    .max(2000, i18n.t('validation.error.below2000')),
  payment: yup.array(),
  paymentFlg: yup
    .string()
    .nullable()
    .required(i18n.t('validation.error.required')),
  seasonalMenuMaterialText: yup
    .string()
    .nullable()
    .max(250, i18n.t('validation.error.below250')),
  similarMediaComparison1: requiredValidation
    .nullable()
    .max(2000, i18n.t('validation.error.below2000')),
  similarMediaComparison2: requiredValidation
    .nullable()
    .max(2000, i18n.t('validation.error.below2000')),
  similarMediaName1: requiredValidation
    .nullable()
    .max(250, i18n.t('validation.error.below250')),
  similarMediaName2: requiredValidation
    .nullable()
    .max(250, i18n.t('validation.error.below250'))
});

export const mediaCaseShema = yup.object().shape({
  clientsBenefits: requiredValidation.max(
    2000,
    i18n.t('validation.error.below2000')
  ),
  continuousFactors1: requiredValidation.max(
    2000,
    i18n.t('validation.error.below2000')
  ),
  continuousFactors2: requiredValidation.max(
    2000,
    i18n.t('validation.error.below2000')
  ),
  continuousFactors3: requiredValidation.max(
    2000,
    i18n.t('validation.error.below2000')
  ),
  industriesNum1: yup
    .number()
    .oneOf(
      [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
        21, 22, 23, 99
      ],
      i18n.t('validation.error.required')
    ),
  industriesNum2: yup
    .number()
    .oneOf(
      [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
        21, 22, 23, 99
      ],
      i18n.t('validation.error.required')
    ),
  industriesNum3: yup
    .number()
    .oneOf(
      [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
        21, 22, 23, 99
      ],
      i18n.t('validation.error.required')
    ),
  industriesOther1: yup.string().when('industriesNum1', {
    is: (value: number) => value === select.other,
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .nullable()
      .max(250, i18n.t('validation.error.below250'))
  }),
  industriesOther2: yup.string().when('industriesNum2', {
    is: (value: number) => value === select.other,
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .nullable()
      .max(250, i18n.t('validation.error.below250'))
  }),
  industriesOther3: yup.string().when('industriesNum3', {
    is: (value: number) => value === select.other,
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .nullable()
      .max(250, i18n.t('validation.error.below250'))
  }),
  industriesPercentage1: yup
    .number()
    .typeError(i18n.t('validation.error.number'))
    .nullable()
    .min(1, i18n.t('validation.error.above1'))
    .max(100, i18n.t('validation.error.below100')),
  industriesPercentage2: yup
    .number()
    .typeError(i18n.t('validation.error.number'))
    .nullable()
    .min(1, i18n.t('validation.error.above1'))
    .max(100, i18n.t('validation.error.below100')),
  industriesPercentage3: yup
    .number()
    .typeError(i18n.t('validation.error.number'))
    .nullable()
    .min(1, i18n.t('validation.error.above1'))
    .max(100, i18n.t('validation.error.below100')),
  mainKpi: yup
    .array()
    .test(
      'checkboxAtLeastOneOf',
      i18n.t('validation.error.atLeastOneOf'),
      (value) => checkboxAtLeastOneOf(value as boolean[])
    ),
  mainKpiOther: yup.string().when('mainKpi.99', {
    is: (value: boolean) => value,
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .nullable()
      .max(250, i18n.t('validation.error.below250'))
  }),
  mediaMenuUrl: urlValidation
    .nullable()
    .max(250, i18n.t('validation.error.below250'))
});
